import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { 
  Plus, Edit, Trash2, DollarSign, Users, HardDrive, Star, Check, X, 
  GripVertical, Sparkles, Calendar, CreditCard, TrendingUp, BarChart3,
  Copy, Eye, Wand2, RefreshCw, AlertCircle, Building, ArrowUpRight,
  ArrowDownRight, Layers, ChevronRight, FileText, Lightbulb, Target,
  PieChart, Activity, Zap, Bot, Clock, RotateCcw, ArrowRightLeft
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EnhancedPricingTiers = () => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [newFeature, setNewFeature] = useState('');
  const [previewMode, setPreviewMode] = useState('monthly');
  const [activeTab, setActiveTab] = useState('tiers');
  const [analytics, setAnalytics] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [draggedTier, setDraggedTier] = useState(null);
  
  // Migration state
  const [migrateDialogOpen, setMigrateDialogOpen] = useState(false);
  const [migratingTier, setMigratingTier] = useState(null);
  const [targetTierId, setTargetTierId] = useState('');
  const [notifyClinics, setNotifyClinics] = useState(true);
  const [migrating, setMigrating] = useState(false);
  
  // Default features that can be toggled - organized by category
  const defaultFeatures = {
    // Core Features
    basic_membership_management: "Basic Membership Management",
    patient_portal: "Patient Portal Access",
    appointment_scheduling: "Appointment Scheduling",
    payment_processing: "Payment Processing",
    email_notifications: "Email Notifications",
    basic_reporting: "Basic Reporting",
    
    // Communication & Automation
    automated_reminders: "Automated Reminders (Email & SMS)",
    secure_messaging: "Secure Messaging",
    
    // AI-Powered Features
    ai_membership_builder: "AI Membership Plan Builder",
    ai_pricing_insights: "AI Dynamic Pricing Insights",
    ai_churn_prediction: "AI Churn Prediction",
    ai_analytics_dashboard: "AI Analytics Dashboard",
    membership_templates: "Pre-built Membership Templates",
    revenue_projections: "AI Revenue Projections",
    
    // Analytics & Data
    advanced_analytics: "Advanced Analytics Dashboard",
    csv_export: "CSV / Excel Export",
    scheduled_reports: "Scheduled Reports",
    
    // Membership Management
    multiple_membership_tiers: "Multiple Membership Tiers",
    payment_plans: "Payment Plans Support",
    medical_notes: "Medical Notes & History",
    treatment_packages: "Treatment Packages",
    service_fees: "Service Fees Management",
    
    // Patient Engagement
    before_after_gallery: "Before/After Gallery",
    consultations: "Consultations Management",
    
    // Integrations & Scalability
    multi_location: "Multi-Location Support",
    stripe_connect: "Stripe Connect Integration",
    wordpress_plugin: "WordPress Plugin",
    api_access: "API Access",
    
    // Team & Operations
    team_management: "Team Management",
    role_based_access: "Role-Based Access Control",
    audit_logs: "Audit Logs",
    
    // Branding & Customization
    custom_branding: "Custom Branding",
    white_label: "White Label Options",
    custom_domain: "Custom Domain",
    
    // Support & Services
    priority_support: "Priority Support",
    onboarding_support: "Onboarding Support",
    training_sessions: "Training Sessions",
    dedicated_manager: "Dedicated Account Manager",
    
    // Enterprise & Compliance
    sla_guarantee: "SLA Guarantee (99.9% Uptime)",
    hipaa_compliance: "HIPAA Compliance Tools",
    custom_integrations: "Custom Integrations",
    unlimited_storage: "Unlimited Storage",
    custom_email_domain: "Custom Email Domain (SendGrid)"
  };

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    monthly_price: '',
    annual_price: '',
    max_members: '',
    max_users: '',
    max_storage_gb: '',
    transaction_fee_percentage: '',
    is_popular: false,
    display_order: 1,
    features: {},
    custom_features: []
  });

  useEffect(() => {
    fetchTiers();
    fetchAnalytics();
    fetchTemplates();
  }, []);

  const fetchTiers = async () => {
    try {
      const token = localStorage.getItem('token');
      // Tier management page needs to see ALL tiers (including inactive) for management
      const response = await axios.get(`${API_URL}/api/subscription-tiers?include_inactive=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTiers(response.data.sort((a, b) => a.display_order - b.display_order));
    } catch (error) {
      console.error('Failed to fetch tiers:', error);
      toast.error('Failed to load pricing tiers');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/tier-analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/tier-analytics/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const runAIAnalysis = async (analysisType) => {
    setAiLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/tier-analytics/ai-analysis`, 
        { analysis_type: analysisType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiAnalysis(response.data);
      toast.success('AI analysis completed');
    } catch (error) {
      console.error('Failed to run AI analysis:', error);
      toast.error('Failed to run AI analysis');
    } finally {
      setAiLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTier(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      monthly_price: '',
      annual_price: '',
      max_members: '100',
      max_users: '5',
      max_storage_gb: '10',
      transaction_fee_percentage: '2.5',
      is_popular: false,
      display_order: tiers.length + 1,
      features: Object.keys(defaultFeatures).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
      custom_features: []
    });
    setDialogOpen(true);
  };

  const handleCreateFromTemplate = (template) => {
    setEditingTier(null);
    setFormData({
      name: template.name,
      slug: template.id,
      description: template.description,
      monthly_price: template.monthly_price.toString(),
      annual_price: template.annual_price.toString(),
      max_members: template.max_members.toString(),
      max_users: template.max_users.toString(),
      max_storage_gb: template.max_storage_gb.toString(),
      transaction_fee_percentage: (template.transaction_fee_percentage * 100).toFixed(1),
      is_popular: template.is_popular || false,
      display_order: tiers.length + 1,
      features: { ...Object.keys(defaultFeatures).reduce((acc, key) => ({ ...acc, [key]: false }), {}), ...template.features },
      custom_features: []
    });
    setShowTemplates(false);
    setDialogOpen(true);
    toast.success(`Template "${template.name}" loaded`);
  };

  const handleEdit = (tier) => {
    setEditingTier(tier);
    
    const customFeatures = [];
    const standardFeatures = {};
    
    Object.entries(tier.features || {}).forEach(([key, value]) => {
      if (defaultFeatures[key]) {
        standardFeatures[key] = value;
      } else if (value === true) {
        customFeatures.push(key);
      }
    });
    
    setFormData({
      name: tier.name || '',
      slug: tier.slug || '',
      description: tier.description || '',
      monthly_price: tier.monthly_price || 0,
      annual_price: tier.annual_price || 0,
      max_members: tier.max_members || 100,
      max_users: tier.max_users || 5,
      max_storage_gb: tier.max_storage_gb || 10,
      transaction_fee_percentage: tier.transaction_fee_percentage ? (tier.transaction_fee_percentage * 100).toFixed(1) : '2.5',
      is_popular: tier.is_popular || false,
      display_order: tier.display_order || 1,
      features: { ...Object.keys(defaultFeatures).reduce((acc, key) => ({ ...acc, [key]: false }), {}), ...standardFeatures },
      custom_features: customFeatures
    });
    setDialogOpen(true);
  };

  const handleClone = async (tier) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/tier-analytics/clone/${tier.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Tier "${tier.name}" cloned successfully`);
      fetchTiers();
    } catch (error) {
      console.error('Failed to clone tier:', error);
      toast.error('Failed to clone tier');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      
      const allFeatures = { ...formData.features };
      formData.custom_features.forEach(feat => {
        allFeatures[feat] = true;
      });
      
      const payload = {
        name: formData.name,
        slug: formData.slug || formData.name.toLowerCase().replace(/\s+/g, '_'),
        description: formData.description,
        monthly_price: parseFloat(formData.monthly_price),
        annual_price: parseFloat(formData.annual_price),
        max_members: parseInt(formData.max_members),
        max_users: parseInt(formData.max_users),
        max_storage_gb: parseInt(formData.max_storage_gb),
        transaction_fee_percentage: parseFloat(formData.transaction_fee_percentage) / 100,
        features: allFeatures,
        is_popular: formData.is_popular,
        display_order: parseInt(formData.display_order)
      };

      if (editingTier) {
        await axios.put(`${API_URL}/api/subscription-tiers/${editingTier.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Pricing tier updated successfully');
      } else {
        await axios.post(`${API_URL}/api/subscription-tiers`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Pricing tier created successfully');
      }

      setDialogOpen(false);
      fetchTiers();
      fetchAnalytics();
    } catch (error) {
      console.error('Failed to save tier:', error);
      toast.error(error.response?.data?.detail || 'Failed to save pricing tier');
    }
  };

  const handleDelete = async (tier) => {
    if (!window.confirm(`Are you sure you want to PERMANENTLY DELETE the "${tier.name}" tier?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/subscription-tiers/${tier.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Tier "${tier.name}" has been permanently deleted`);
      await fetchTiers();
      fetchAnalytics();
    } catch (error) {
      console.error('Failed to delete tier:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete pricing tier');
    }
  };

  const handleReactivate = async (tier) => {
    if (!window.confirm(`Are you sure you want to reactivate the "${tier.name}" tier?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/subscription-tiers/${tier.id}`, 
        { ...tier, is_active: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Pricing tier reactivated successfully');
      await fetchTiers();
      fetchAnalytics();
    } catch (error) {
      console.error('Failed to reactivate tier:', error);
      toast.error(error.response?.data?.detail || 'Failed to reactivate pricing tier');
    }
  };

  const openMigrateDialog = (tier) => {
    setMigratingTier(tier);
    setTargetTierId('');
    setNotifyClinics(true);
    setMigrateDialogOpen(true);
  };

  const handleMigrateSubscribers = async () => {
    if (!targetTierId) {
      toast.error('Please select a target tier');
      return;
    }

    setMigrating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/subscription-tiers/${migratingTier.id}/migrate-subscribers`,
        { target_tier_id: targetTierId, notify_clinics: notifyClinics },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setMigrateDialogOpen(false);
      setMigratingTier(null);
      await fetchTiers();
      fetchAnalytics();
    } catch (error) {
      console.error('Failed to migrate subscribers:', error);
      toast.error(error.response?.data?.detail || 'Failed to migrate subscribers');
    } finally {
      setMigrating(false);
    }
  };

  const getSubscriberCount = (tier) => {
    if (!tier) return 0;
    const tierAnalytics = analytics?.tiers?.find(t => t.tier_id === tier.id);
    return tierAnalytics?.total_clinics || 0;
  };

  const handleFeatureToggle = (featureKey) => {
    setFormData(prev => {
      const currentFeatures = prev.features || {};
      return {
        ...prev,
        features: {
          ...currentFeatures,
          [featureKey]: !currentFeatures[featureKey]
        }
      };
    });
  };

  const addCustomFeature = () => {
    if (newFeature.trim() && !formData.custom_features.includes(newFeature.trim())) {
      setFormData(prev => ({
        ...prev,
        custom_features: [...prev.custom_features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const removeCustomFeature = (feature) => {
    setFormData(prev => ({
      ...prev,
      custom_features: prev.custom_features.filter(f => f !== feature)
    }));
  };

  const calculateYearlySavings = (monthly, yearly) => {
    const monthlyTotal = monthly * 12;
    const yearlyTotal = yearly * 12;
    const savings = monthlyTotal - yearlyTotal;
    const percentage = ((savings / monthlyTotal) * 100).toFixed(0);
    return { savings, percentage };
  };

  const getEnabledFeatures = (tier) => {
    return Object.entries(tier.features || {})
      .filter(([_, enabled]) => enabled)
      .map(([key, _]) => defaultFeatures[key] || key.replace(/_/g, ' '));
  };

  // Drag and drop handlers
  const handleDragStart = (e, tier) => {
    setDraggedTier(tier);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetTier) => {
    e.preventDefault();
    if (!draggedTier || draggedTier.id === targetTier.id) return;

    const newTiers = [...tiers];
    const draggedIndex = newTiers.findIndex(t => t.id === draggedTier.id);
    const targetIndex = newTiers.findIndex(t => t.id === targetTier.id);

    newTiers.splice(draggedIndex, 1);
    newTiers.splice(targetIndex, 0, draggedTier);

    // Update display orders
    const tierOrders = newTiers.map((tier, index) => ({
      tier_id: tier.id,
      order: index + 1
    }));

    setTiers(newTiers);
    setDraggedTier(null);

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/tier-analytics/reorder`, tierOrders, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tiers reordered');
    } catch (error) {
      console.error('Failed to reorder:', error);
      fetchTiers(); // Revert on error
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Subscription Pricing Management</h2>
          <p className="text-gray-600 mt-1">Manage pricing tiers with analytics and AI-powered insights</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowTemplates(true)}>
            <FileText className="h-4 w-4 mr-2" />
            Templates
          </Button>
          <Button onClick={handleCreate} className="bg-teal-600 hover:bg-teal-700">
            <Plus className="h-4 w-4 mr-2" />
            Create Tier
          </Button>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="tiers" className="flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Pricing Tiers
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="comparison" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            Comparison
          </TabsTrigger>
          <TabsTrigger value="ai" className="flex items-center gap-2">
            <Bot className="h-4 w-4" />
            AI Insights
          </TabsTrigger>
        </TabsList>

        {/* Pricing Tiers Tab */}
        <TabsContent value="tiers" className="space-y-6">
          {/* Preview Toggle */}
          <div className="flex items-center justify-center gap-4 p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl border border-teal-100">
            <span className={`font-medium ${previewMode === 'monthly' ? 'text-teal-700' : 'text-gray-500'}`}>Monthly</span>
            <Switch
              checked={previewMode === 'yearly'}
              onCheckedChange={(checked) => setPreviewMode(checked ? 'yearly' : 'monthly')}
            />
            <span className={`font-medium ${previewMode === 'yearly' ? 'text-teal-700' : 'text-gray-500'}`}>
              Yearly
              <Badge className="ml-2 bg-green-100 text-green-700 hover:bg-green-100">Save up to 20%</Badge>
            </span>
          </div>

          {/* Tier Cards */}
          {tiers.length === 0 ? (
            <Card className="p-12 text-center">
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center">
                  <CreditCard className="h-8 w-8 text-teal-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">No Pricing Tiers Yet</h3>
                <p className="text-gray-600 max-w-md">Create your first pricing tier or use a template to get started.</p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowTemplates(true)}>
                    <FileText className="h-4 w-4 mr-2" />
                    Use Template
                  </Button>
                  <Button onClick={handleCreate} className="bg-teal-600 hover:bg-teal-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Tier
                  </Button>
                </div>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {tiers.map((tier, index) => {
                const savings = calculateYearlySavings(tier.monthly_price, tier.annual_price);
                const enabledFeatures = getEnabledFeatures(tier);
                const tierAnalytics = analytics?.tiers?.find(t => t.tier_id === tier.id);
                
                return (
                  <Card 
                    key={tier.id} 
                    draggable
                    onDragStart={(e) => handleDragStart(e, tier)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, tier)}
                    className={`relative overflow-hidden transition-all duration-300 hover:shadow-lg cursor-move ${
                      tier.is_active === false
                        ? 'border-2 border-dashed border-gray-300 bg-gray-50 opacity-75'
                        : tier.is_popular 
                          ? 'border-2 border-teal-500 shadow-teal-100 shadow-lg' 
                          : 'border border-gray-200'
                    } ${draggedTier?.id === tier.id ? 'opacity-50' : ''}`}
                  >
                    {/* Drag Handle */}
                    <div className="absolute top-2 left-2 text-gray-400">
                      <GripVertical className="h-4 w-4" />
                    </div>

                    {/* Inactive Badge */}
                    {tier.is_active === false && (
                      <div className="absolute top-0 right-0">
                        <div className="bg-gray-500 text-white px-4 py-1 text-xs font-bold rounded-bl-lg flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          INACTIVE
                        </div>
                      </div>
                    )}

                    {/* Popular Badge */}
                    {tier.is_popular && tier.is_active !== false && (
                      <div className="absolute top-0 right-0">
                        <div className="bg-gradient-to-r from-amber-400 to-orange-400 text-white px-4 py-1 text-xs font-bold rounded-bl-lg flex items-center gap-1">
                          <Star className="h-3 w-3 fill-current" />
                          MOST POPULAR
                        </div>
                      </div>
                    )}
                    
                    <CardHeader className="pb-2 pt-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-xl">{tier.name}</CardTitle>
                          <CardDescription className="mt-1 line-clamp-2">{tier.description}</CardDescription>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          #{tier.display_order}
                        </Badge>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      {/* Pricing */}
                      <div className="py-4 border-y border-gray-100">
                        {previewMode === 'monthly' ? (
                          <div>
                            <div className="flex items-baseline gap-1">
                              <span className="text-4xl font-bold text-gray-900">${tier.monthly_price.toFixed(0)}</span>
                              <span className="text-gray-500">/month</span>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">Billed monthly</p>
                          </div>
                        ) : (
                          <div>
                            <div className="flex items-baseline gap-1">
                              <span className="text-4xl font-bold text-gray-900">${tier.annual_price.toFixed(0)}</span>
                              <span className="text-gray-500">/month</span>
                            </div>
                            <p className="text-sm text-green-600 mt-1 font-medium">
                              Save ${savings.savings.toFixed(0)}/year ({savings.percentage}% off)
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Analytics Badge */}
                      {tierAnalytics && (
                        <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
                          <Building className="h-4 w-4 text-blue-600" />
                          <span className="text-sm text-blue-700 font-medium">
                            {tierAnalytics.total_clinics} clinics
                          </span>
                          <span className="text-xs text-blue-500">
                            ({tierAnalytics.market_share}% share)
                          </span>
                        </div>
                      )}

                      {/* Limits */}
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-gray-700">
                          <Users className="h-4 w-4 text-teal-600" />
                          <span>{tier.max_members === -1 ? 'Unlimited' : tier.max_members.toLocaleString()} patients</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-700">
                          <Users className="h-4 w-4 text-teal-600" />
                          <span>{tier.max_users === -1 ? 'Unlimited' : tier.max_users} staff</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-700">
                          <HardDrive className="h-4 w-4 text-teal-600" />
                          <span>{tier.max_storage_gb}GB storage</span>
                        </div>
                      </div>

                      {/* Features Preview */}
                      <div className="pt-3 border-t border-gray-100">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                          {enabledFeatures.length} Features Included
                        </p>
                        <div className="space-y-1">
                          {enabledFeatures.slice(0, 4).map((feature, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                              <Check className="h-3.5 w-3.5 text-teal-500 flex-shrink-0" />
                              <span className="truncate">{feature}</span>
                            </div>
                          ))}
                          {enabledFeatures.length > 4 && (
                            <p className="text-xs text-teal-600 font-medium mt-1">
                              +{enabledFeatures.length - 4} more
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 pt-4">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleEdit(tier)} 
                          className="flex-1"
                          data-testid={`edit-tier-${tier.id}`}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleClone(tier)}
                          className="px-2"
                          title="Clone tier"
                          data-testid={`clone-tier-${tier.id}`}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        {/* Migrate button - only show if tier has subscribers */}
                        {getSubscriberCount(tier) > 0 && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => openMigrateDialog(tier)}
                            className="px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
                            title="Migrate subscribers to another tier"
                            data-testid={`migrate-tier-${tier.id}`}
                          >
                            <ArrowRightLeft className="h-4 w-4" />
                          </Button>
                        )}
                        {tier.is_active === false ? (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleReactivate(tier)} 
                            className="px-2 text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200"
                            title="Reactivate tier"
                            data-testid={`reactivate-tier-${tier.id}`}
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleDelete(tier)} 
                            className="px-2 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                            title="Delete tier permanently"
                            data-testid={`delete-tier-${tier.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          {analytics ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="p-4 bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                      <DollarSign className="h-5 w-5 text-teal-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Monthly Revenue</p>
                      <p className="text-2xl font-bold text-gray-900">${analytics.summary.total_mrr.toLocaleString()}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Annual Revenue</p>
                      <p className="text-2xl font-bold text-gray-900">${analytics.summary.total_arr.toLocaleString()}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 border-purple-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <Building className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Total Clinics</p>
                      <p className="text-2xl font-bold text-gray-900">{analytics.summary.total_clinics}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <Activity className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Avg Revenue/Clinic</p>
                      <p className="text-2xl font-bold text-gray-900">${analytics.summary.avg_revenue_per_clinic.toFixed(0)}</p>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Tier Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="h-5 w-5 text-teal-600" />
                    Revenue by Tier
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {analytics.tiers.map((tier, idx) => (
                      <div key={idx} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{tier.tier_name}</span>
                            <Badge variant="outline" className="text-xs">
                              {tier.total_clinics} clinics
                            </Badge>
                          </div>
                          <div className="text-right">
                            <span className="font-bold text-teal-600">${tier.mrr.toLocaleString()}</span>
                            <span className="text-gray-500 text-sm">/mo</span>
                          </div>
                        </div>
                        <Progress 
                          value={tier.market_share} 
                          className="h-2"
                        />
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>{tier.market_share}% market share</span>
                          <span>${tier.arr.toLocaleString()}/year</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Trends */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="h-5 w-5 text-amber-500" />
                    <span className="font-semibold">Most Popular Tier</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{analytics.trends.most_popular_tier}</p>
                  <p className="text-sm text-gray-500">By clinic count</p>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-5 w-5 text-green-500" />
                    <span className="font-semibold">Highest Revenue Tier</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{analytics.trends.highest_revenue_tier}</p>
                  <p className="text-sm text-gray-500">By monthly revenue</p>
                </Card>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <RefreshCw className="h-8 w-8 text-gray-400 mx-auto mb-2 animate-spin" />
                <p className="text-gray-500">Loading analytics...</p>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Comparison Tab */}
        <TabsContent value="comparison" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Tier Comparison Matrix</CardTitle>
              <CardDescription>Side-by-side comparison of all pricing tiers</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Feature</th>
                      {tiers.map(tier => (
                        <th key={tier.id} className="text-center py-3 px-4">
                          <div className="font-semibold text-gray-900">{tier.name}</div>
                          <div className="text-teal-600 font-bold">${tier.monthly_price}/mo</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b bg-gray-50">
                      <td className="py-2 px-4 font-medium">Max Patients</td>
                      {tiers.map(tier => (
                        <td key={tier.id} className="text-center py-2 px-4">
                          {tier.max_members === -1 ? 'Unlimited' : tier.max_members.toLocaleString()}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-4 font-medium">Max Staff</td>
                      {tiers.map(tier => (
                        <td key={tier.id} className="text-center py-2 px-4">
                          {tier.max_users === -1 ? 'Unlimited' : tier.max_users}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b bg-gray-50">
                      <td className="py-2 px-4 font-medium">Storage</td>
                      {tiers.map(tier => (
                        <td key={tier.id} className="text-center py-2 px-4">
                          {tier.max_storage_gb}GB
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-4 font-medium">Transaction Fee</td>
                      {tiers.map(tier => (
                        <td key={tier.id} className="text-center py-2 px-4">
                          {(tier.transaction_fee_percentage * 100).toFixed(1)}%
                        </td>
                      ))}
                    </tr>
                    {Object.entries(defaultFeatures).slice(0, 15).map(([key, label]) => (
                      <tr key={key} className="border-b">
                        <td className="py-2 px-4">{label}</td>
                        {tiers.map(tier => (
                          <td key={tier.id} className="text-center py-2 px-4">
                            {tier.features?.[key] ? (
                              <Check className="h-5 w-5 text-green-500 mx-auto" />
                            ) : (
                              <X className="h-5 w-5 text-gray-300 mx-auto" />
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Insights Tab */}
        <TabsContent value="ai" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5 text-teal-600" />
                AI-Powered Pricing Analysis
              </CardTitle>
              <CardDescription>Get intelligent recommendations to optimize your pricing strategy</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => runAIAnalysis('pricing')}
                  disabled={aiLoading}
                  className="h-auto py-4 flex flex-col items-center gap-2"
                >
                  <DollarSign className="h-6 w-6 text-teal-600" />
                  <span className="text-sm font-medium">Pricing Analysis</span>
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => runAIAnalysis('features')}
                  disabled={aiLoading}
                  className="h-auto py-4 flex flex-col items-center gap-2"
                >
                  <Layers className="h-6 w-6 text-blue-600" />
                  <span className="text-sm font-medium">Feature Bundling</span>
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => runAIAnalysis('comparison')}
                  disabled={aiLoading}
                  className="h-auto py-4 flex flex-col items-center gap-2"
                >
                  <Target className="h-6 w-6 text-purple-600" />
                  <span className="text-sm font-medium">Gap Analysis</span>
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => runAIAnalysis('optimization')}
                  disabled={aiLoading}
                  className="h-auto py-4 flex flex-col items-center gap-2"
                >
                  <Zap className="h-6 w-6 text-amber-600" />
                  <span className="text-sm font-medium">Full Optimization</span>
                </Button>
              </div>

              {aiLoading && (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Wand2 className="h-10 w-10 text-teal-600 mx-auto mb-3 animate-pulse" />
                    <p className="text-gray-600 font-medium">Analyzing your pricing data...</p>
                    <p className="text-sm text-gray-500">This may take a few moments</p>
                  </div>
                </div>
              )}

              {aiAnalysis && !aiLoading && (
                <div className="mt-6 p-4 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl border border-teal-100">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="h-5 w-5 text-teal-600" />
                    <span className="font-semibold text-gray-900">AI Recommendations</span>
                    <Badge variant="outline" className="text-xs">
                      {aiAnalysis.analysis_type}
                    </Badge>
                  </div>
                  
                  <div className="prose prose-sm max-w-none">
                    {aiAnalysis.analysis?.recommendations && (
                      <div className="space-y-3">
                        {aiAnalysis.analysis.recommendations.map((rec, idx) => (
                          <div key={idx} className="p-3 bg-white rounded-lg shadow-sm">
                            <div className="flex justify-between items-start mb-2">
                              <span className="font-medium text-gray-900">{rec.tier_name}</span>
                              <Badge className={
                                rec.confidence === 'high' ? 'bg-green-100 text-green-700' :
                                rec.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-700'
                              }>
                                {rec.confidence} confidence
                              </Badge>
                            </div>
                            {rec.recommended_price && rec.current_price && (
                              <div className="flex items-center gap-2 text-sm mb-2">
                                <span className="text-gray-500">${rec.current_price}</span>
                                <ArrowUpRight className="h-4 w-4 text-green-500" />
                                <span className="font-bold text-green-600">${rec.recommended_price}</span>
                                <span className="text-xs text-gray-500">
                                  ({rec.change_percentage > 0 ? '+' : ''}{rec.change_percentage}%)
                                </span>
                              </div>
                            )}
                            <p className="text-sm text-gray-600">{rec.reasoning}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {aiAnalysis.analysis?.overall_insights && (
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Lightbulb className="h-4 w-4 text-blue-600" />
                          <span className="font-medium text-blue-900">Key Insights</span>
                        </div>
                        <p className="text-sm text-blue-800">{aiAnalysis.analysis.overall_insights}</p>
                      </div>
                    )}

                    {aiAnalysis.analysis?.action_items && (
                      <div className="mt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Check className="h-4 w-4 text-teal-600" />
                          <span className="font-medium text-gray-900">Action Items</span>
                        </div>
                        <ul className="space-y-1">
                          {aiAnalysis.analysis.action_items.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                              <ChevronRight className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {aiAnalysis.analysis?.raw_analysis && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <pre className="text-xs text-gray-600 whitespace-pre-wrap overflow-x-auto">
                          {aiAnalysis.analysis.raw_analysis}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Templates Dialog */}
      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-teal-600" />
              Tier Templates
            </DialogTitle>
            <DialogDescription>
              Start with a pre-built template to quickly create a new pricing tier
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {templates.map((template) => (
              <Card 
                key={template.id} 
                className="cursor-pointer hover:border-teal-500 transition-colors"
                onClick={() => handleCreateFromTemplate(template)}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-gray-900">{template.name}</h3>
                    <Badge variant="outline">${template.monthly_price}/mo</Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <Badge variant="secondary">
                      {template.max_members === -1 ? 'Unlimited' : template.max_members} patients
                    </Badge>
                    <Badge variant="secondary">
                      {template.max_users === -1 ? 'Unlimited' : template.max_users} staff
                    </Badge>
                    <Badge variant="secondary">
                      {Object.values(template.features || {}).filter(Boolean).length} features
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    <span className="font-medium">Best for:</span> {template.recommended_for}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              {editingTier ? (
                <>
                  <Edit className="h-5 w-5 text-teal-600" />
                  Edit Pricing Tier
                </>
              ) : (
                <>
                  <Sparkles className="h-5 w-5 text-teal-600" />
                  Create New Pricing Tier
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {editingTier 
                ? 'Update the pricing tier details and features' 
                : 'Define a new subscription tier with pricing and features for clinics'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="pricing">Pricing & Limits</TabsTrigger>
                <TabsTrigger value="features">Features</TabsTrigger>
              </TabsList>

              {/* Basic Info Tab */}
              <TabsContent value="basic" className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tier Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Professional"
                      required
                    />
                  </div>
                  <div>
                    <Label>Slug (URL-friendly)</Label>
                    <Input
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                      placeholder="e.g., professional"
                    />
                  </div>
                </div>

                <div>
                  <Label>Description *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of what this tier offers..."
                    rows={3}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <Switch
                      checked={formData.is_popular}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_popular: checked })}
                    />
                    <div>
                      <Label className="flex items-center gap-2">
                        <Star className="h-4 w-4 text-amber-500" />
                        Mark as "Most Popular"
                      </Label>
                      <p className="text-xs text-gray-500">Highlights this tier</p>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg border">
                    <Label>Display Order</Label>
                    <Input
                      type="number"
                      value={formData.display_order}
                      onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
                      min="1"
                      className="mt-1"
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Pricing & Limits Tab */}
              <TabsContent value="pricing" className="space-y-6 pt-4">
                <div className="p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl border border-teal-100">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-teal-600" />
                    Pricing
                  </h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <Label>Monthly Price ($) *</Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.monthly_price}
                          onChange={(e) => {
                            const monthly = parseFloat(e.target.value) || 0;
                            setFormData({ 
                              ...formData, 
                              monthly_price: e.target.value,
                              annual_price: formData.annual_price || (monthly * 0.8).toFixed(0)
                            });
                          }}
                          className="pl-8"
                          placeholder="99.00"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Yearly Price ($/month) *</Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.annual_price}
                          onChange={(e) => setFormData({ ...formData, annual_price: e.target.value })}
                          className="pl-8"
                          placeholder="79.00"
                          required
                        />
                      </div>
                      {formData.monthly_price && formData.annual_price && (
                        <p className="text-xs text-green-600 mt-1 font-medium">
                          {((1 - parseFloat(formData.annual_price) / parseFloat(formData.monthly_price)) * 100).toFixed(0)}% discount • Saves ${((parseFloat(formData.monthly_price) - parseFloat(formData.annual_price)) * 12).toFixed(0)}/year
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-xl border">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Users className="h-5 w-5 text-gray-600" />
                    Usage Limits
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Max Patients *</Label>
                      <Input
                        type="number"
                        value={formData.max_members}
                        onChange={(e) => setFormData({ ...formData, max_members: e.target.value })}
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Use -1 for unlimited</p>
                    </div>
                    <div>
                      <Label>Max Staff Accounts *</Label>
                      <Input
                        type="number"
                        value={formData.max_users}
                        onChange={(e) => setFormData({ ...formData, max_users: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Storage (GB) *</Label>
                      <Input
                        type="number"
                        value={formData.max_storage_gb}
                        onChange={(e) => setFormData({ ...formData, max_storage_gb: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Transaction Fee (%) *</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.transaction_fee_percentage}
                        onChange={(e) => setFormData({ ...formData, transaction_fee_percentage: e.target.value })}
                        placeholder="2.5"
                        required
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Features Tab */}
              <TabsContent value="features" className="space-y-6 pt-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Standard Features</h3>
                  <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto border rounded-lg p-4 bg-gray-50">
                    {Object.entries(defaultFeatures).map(([key, label]) => (
                      <div 
                        key={key} 
                        className={`flex items-center space-x-2 p-2 rounded transition-colors ${
                          formData.features?.[key] ? 'bg-teal-50' : 'hover:bg-gray-100'
                        }`}
                      >
                        <Switch
                          checked={Boolean(formData.features?.[key])}
                          onCheckedChange={() => handleFeatureToggle(key)}
                        />
                        <Label 
                          className="text-sm cursor-pointer flex-1"
                          onClick={() => handleFeatureToggle(key)}
                        >
                          {label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Custom Features</h3>
                  <div className="flex gap-2 mb-4">
                    <Input
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      placeholder="Enter custom feature name..."
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomFeature())}
                    />
                    <Button type="button" onClick={addCustomFeature} variant="outline">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {formData.custom_features.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.custom_features.map((feature, idx) => (
                        <Badge 
                          key={idx} 
                          variant="secondary" 
                          className="pl-3 pr-1 py-1.5 flex items-center gap-2 bg-teal-100 text-teal-800"
                        >
                          <Check className="h-3 w-3" />
                          {feature}
                          <button
                            type="button"
                            onClick={() => removeCustomFeature(feature)}
                            className="ml-1 hover:bg-teal-200 rounded-full p-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-sm text-blue-800">
                    <strong>Summary:</strong> {Object.values(formData.features).filter(Boolean).length + formData.custom_features.length} features enabled
                  </p>
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                {editingTier ? 'Update Tier' : 'Create Tier'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Migrate Subscribers Dialog */}
      <Dialog open={migrateDialogOpen} onOpenChange={setMigrateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-blue-600" />
              Migrate Subscribers
            </DialogTitle>
            <DialogDescription>
              Move all clinics from "{migratingTier?.name}" to another tier
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Source tier info */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">From:</p>
              <p className="font-semibold text-gray-900">{migratingTier?.name}</p>
              <p className="text-sm text-gray-500">
                {getSubscriberCount(migratingTier)} clinic(s) will be migrated
              </p>
            </div>

            {/* Target tier selection */}
            <div className="space-y-2">
              <Label>Target Tier *</Label>
              <Select value={targetTierId} onValueChange={setTargetTierId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select target tier" />
                </SelectTrigger>
                <SelectContent>
                  {tiers
                    .filter(t => t.id !== migratingTier?.id && t.is_active !== false)
                    .map(tier => (
                      <SelectItem key={tier.id} value={tier.id}>
                        {tier.name} (${tier.monthly_price}/mo)
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>

            {/* Notification option */}
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div>
                <Label className="text-sm font-medium">Notify clinics via email</Label>
                <p className="text-xs text-gray-500">Send email notification about the change</p>
              </div>
              <Switch
                checked={notifyClinics}
                onCheckedChange={setNotifyClinics}
              />
            </div>

            {/* Warning */}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Important</p>
                  <p>This will immediately update all clinic subscriptions. Billing changes may apply based on the new tier pricing.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => setMigrateDialogOpen(false)}
              disabled={migrating}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleMigrateSubscribers}
              disabled={!targetTierId || migrating}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {migrating ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Migrating...
                </>
              ) : (
                <>
                  <ArrowRightLeft className="h-4 w-4 mr-2" />
                  Migrate Subscribers
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnhancedPricingTiers;
